import { useRef, useEffect } from "react";
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link2, Quote, Image } from "lucide-react";
import api from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { toast } from "sonner";

export default function RichTextEditor({ value, onChange, testId = "rich-editor" }) {
  const ref = useRef();

  useEffect(() => {
    const clean = sanitizeHtml(value || "");
    if (ref.current && ref.current.innerHTML !== clean) {
      ref.current.innerHTML = clean;
    }
  }, []); // eslint-disable-line

  const emit = () => onChange(ref.current.innerHTML);
  const cmd = (command, arg) => { document.execCommand(command, false, arg); ref.current.focus(); emit(); };

  const applyFontSize = (em) => {
    if (!em) return;
    document.execCommand("fontSize", false, "7");
    const fonts = ref.current.querySelectorAll('font[size="7"]');
    fonts.forEach((f) => {
      const span = document.createElement("span");
      span.style.fontSize = em;
      span.innerHTML = f.innerHTML;
      f.replaceWith(span);
    });
    ref.current.focus();
    emit();
  };

  const addLink = () => {
    const url = window.prompt("Link URL:");
    if (url) cmd("createLink", url);
  };

  const addImage = async () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        cmd("insertHTML", `<img src="${url}" alt="${file.name}" />`);
      } catch (e) { toast.error("Image upload failed"); }
    };
    input.click();
  };

  const Btn = ({ onClick, children, label }) => (
    <button type="button" onClick={onClick} title={label} data-testid={`${testId}-${label}`} className="flex h-8 w-8 items-center justify-center rounded hover:bg-black/10 dark:hover:bg-white/10">
      {children}
    </button>
  );

  return (
    <div className="rounded-md border" style={{ backgroundColor: "var(--arc-card)" }}>
      <div className="flex flex-wrap items-center gap-1 border-b p-1.5">
        <Btn onClick={() => cmd("bold")} label="bold"><Bold size={15} /></Btn>
        <Btn onClick={() => cmd("italic")} label="italic"><Italic size={15} /></Btn>
        <Btn onClick={() => cmd("formatBlock", "<h2>")} label="h2"><Heading2 size={15} /></Btn>
        <Btn onClick={() => cmd("formatBlock", "<h3>")} label="h3"><Heading3 size={15} /></Btn>
        <Btn onClick={() => cmd("insertUnorderedList")} label="ul"><List size={15} /></Btn>
        <Btn onClick={() => cmd("insertOrderedList")} label="ol"><ListOrdered size={15} /></Btn>
        <Btn onClick={() => cmd("formatBlock", "<blockquote>")} label="quote"><Quote size={15} /></Btn>
        <select
          data-testid={`${testId}-fontsize`}
          onChange={(e) => { applyFontSize(e.target.value); e.target.selectedIndex = 0; }}
          className="h-8 rounded border bg-transparent px-1 text-xs"
          style={{ color: "var(--arc-text)" }}
          title="Font size (applies to selected text)"
          defaultValue=""
        >
          <option value="" disabled>Size</option>
          <option value="0.85em">Small</option>
          <option value="1em">Normal</option>
          <option value="1.35em">Large</option>
          <option value="1.75em">X-Large</option>
          <option value="2.25em">Huge</option>
        </select>
        <Btn onClick={addLink} label="link"><Link2 size={15} /></Btn>
        <Btn onClick={addImage} label="image"><Image size={15} /></Btn>
      </div>
      <div
        ref={ref}
        data-testid={testId}
        contentEditable
        onInput={emit}
        onBlur={emit}
        className="article-body min-h-[140px] px-4 py-3 text-sm outline-none"
        suppressContentEditableWarning
      />
    </div>
  );
}
